import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'Guide', path: '/hipobuy-spreadsheet-guide' },
    { label: 'Review', path: '/hipobuy-spreadsheet-review' },
    { label: 'Safety', path: '/spreadsheet-safety-guide' },
    { label: 'Comparison', path: '/hipobuy-spreadsheet-comparison' },
    { label: 'Beginners', path: '/hipobuy-spreadsheet-beginner-guide' },
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 h-[72px] bg-background-50/85 backdrop-blur-md border-b border-background-200/60">
        <div className="max-w-[1400px] mx-auto h-full px-6 md:px-10 flex items-center justify-between">
          <Link
            to="/"
            className="font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-tight whitespace-nowrap cursor-pointer"
            onClick={() => setMobileOpen(false)}
          >
            Hipobuy Spread
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-4">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-primary-500 text-primary-700 font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-50 transition-colors duration-200"
              >
                <span>Categories</span>
                {dropdownOpen ? (
                  <i className="ri-arrow-up-s-line text-base"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-base"></i>
                )}
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 bg-background-50 rounded-2xl border border-background-200/80 py-3 px-2 min-w-[320px] shadow-lg z-50">
                  <div className="grid grid-cols-2 gap-0.5">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 cursor-pointer group whitespace-nowrap"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-400 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex-shrink-0"></span>
                        <i className={`${cat.icon} text-base text-foreground-400 group-hover:text-primary-500 transition-colors duration-150`}></i>
                        <span>{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors duration-200"
            >
              Hipobuy Sheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full border-2 border-primary-500 text-primary-700 font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-50 transition-colors duration-200"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
            aria-label="Toggle mobile menu"
          >
            {mobileOpen ? (
              <i className="ri-close-line text-2xl"></i>
            ) : (
              <i className="ri-menu-line text-2xl"></i>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          ></div>
          <div
            ref={mobileRef}
            className="absolute top-[72px] left-0 right-0 bottom-0 bg-background-50 overflow-y-auto"
          >
            <div className="px-6 py-6 space-y-8">
              {/* CTA Buttons */}
              <div className="flex flex-col gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="w-full px-5 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm text-center whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors duration-200"
                >
                  Hipobuy Sheet
                  <i className="ri-arrow-right-up-line ml-1.5"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="w-full px-5 py-3 rounded-full border-2 border-primary-500 text-primary-700 font-semibold text-sm text-center whitespace-nowrap cursor-pointer hover:bg-primary-50 transition-colors duration-200"
                >
                  Buy It Now
                  <i className="ri-arrow-right-up-line ml-1.5"></i>
                </a>
              </div>

              {/* Page Navigation */}
              <div>
                <p className="text-xs uppercase tracking-wider text-foreground-500 font-semibold mb-3">Navigation</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 cursor-pointer"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Product Categories */}
              <div>
                <p className="text-xs uppercase tracking-wider text-foreground-500 font-semibold mb-3">Product Categories</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 cursor-pointer"
                    >
                      <i className={`${cat.icon} text-base text-foreground-400`}></i>
                      <span className="truncate">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Footer Note */}
              <div className="pt-4 border-t border-background-200/60">
                <p className="text-xs text-foreground-400 leading-relaxed">
                  Independent educational resource. Not affiliated with any marketplace.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}